/**
 * ModuleGroups - Componente para mostrar grupos de módulos estilo Chrome
 * Adaptado de NewLnF/components/ModuleGroups.tsx para AdminConsole (JavaScript + MUI)
 * Muestra hasta 4 grupos de aplicaciones como círculos coloreados en la navbar
 */

import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  TextField,
  Divider,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  MoreHoriz as MoreHorizIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useAppRegistryStore } from "../../stores/appRegistryStore";
import "./ModuleGroups.css";

const GROUP_COLORS = [
  "#3B82F6", // Blue
  "#10B981", // Green
  "#F59E0B", // Yellow
  "#EF4444", // Red
  "#8B5CF6", // Purple
  "#06B6D4", // Cyan
  "#F97316", // Orange
  "#84CC16", // Lime
];

const ModuleGroups = ({ className = "", onAppClick }) => {
  // Suscribirse específicamente a los cambios que necesitamos
  const apps = useAppRegistryStore((state) => state.apps);
  const settings = useAppRegistryStore((state) => state.settings);
  const createModuleGroup = useAppRegistryStore((state) => state.createModuleGroup);
  const updateModuleGroup = useAppRegistryStore((state) => state.updateModuleGroup);
  const deleteModuleGroup = useAppRegistryStore((state) => state.deleteModuleGroup);
  const reorderModuleGroups = useAppRegistryStore((state) => state.reorderModuleGroups);
  const setActiveGroup = useAppRegistryStore((state) => state.setActiveGroup);
  const accessApp = useAppRegistryStore((state) => state.accessApp);
  const removeModuleFromGroup = useAppRegistryStore((state) => state.removeModuleFromGroup);
  const categories = useAppRegistryStore((state) => state.categories);

  const [isDragging, setIsDragging] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [dragOverTarget, setDragOverTarget] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const isDarkMode = settings?.currentTheme === "dark";

  const { pinnedApps, groups } = useMemo(() => {
    const appsList = apps.filter((app) => app.pinned).slice(0, 7);
    const appMap = new Map(appsList.map((app) => [app.id, app]));

    const moduleGroups = (settings.moduleGroups?.groups || []).map((group) => {
      const resolvedModules = group.modules
        .map((moduleRef) => {
          const app = appMap.get(moduleRef.id);
          return app || null;
        })
        .filter((app) => app !== null);

      return {
        id: group.id,
        name: group.name,
        color: group.color,
        modules: resolvedModules,
      };
    });

    return { pinnedApps: appsList, groups: moduleGroups };
  }, [apps, settings.moduleGroups?.groups]);

  const expandedGroupId = settings.moduleGroups?.activeGroupId;

  // Debug logs
  console.log("[ModuleGroups] expandedGroupId:", expandedGroupId);
  console.log("[ModuleGroups] groups:", groups);
  console.log("[ModuleGroups] expandedGroup:", groups.find((g) => g.id === expandedGroupId));

  const groupedModuleIds = groups.flatMap((g) => g.modules.map((m) => m.id));
  const ungroupedApps = pinnedApps.filter((app) => !groupedModuleIds.includes(app.id));

  const handleDragStart = useCallback((e, data) => {
    setIsDragging(true);
    e.dataTransfer.setData("application/json", JSON.stringify(data));
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDragOverTarget(null);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleDrop = useCallback(
    (e, targetGroupId, targetIndex) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOverTarget(null);

      try {
        const dragDataString = e.dataTransfer.getData("application/json");
        if (!dragDataString) return;

        const dragData = JSON.parse(dragDataString);

        if (dragData.type === "app") {
          const app = pinnedApps.find((a) => a.id === dragData.id);
          if (!app) return;

          if (dragData.sourceGroupId === targetGroupId && targetIndex !== undefined) {
            const targetGroup = groups.find((g) => g.id === targetGroupId);
            if (targetGroup) {
              const newModules = [...targetGroup.modules];
              const [movedApp] = newModules.splice(dragData.sourceIndex, 1);
              newModules.splice(targetIndex, 0, movedApp);

              updateModuleGroup(targetGroupId, {
                ...targetGroup,
                modules: newModules.map((m) => ({ id: m.id, name: m.name })),
              });
            }
            return;
          }

          if (dragData.sourceGroupId && dragData.sourceGroupId !== targetGroupId) {
            const sourceGroup = groups.find((g) => g.id === dragData.sourceGroupId);
            if (sourceGroup) {
              const updatedModules = sourceGroup.modules.filter((m) => m.id !== dragData.id);
              updateModuleGroup(dragData.sourceGroupId, {
                ...sourceGroup,
                modules: updatedModules.map((m) => ({ id: m.id, name: m.name })),
              });
            }
          }

          if (targetGroupId) {
            const targetGroup = groups.find((g) => g.id === targetGroupId);
            if (targetGroup && targetGroup.modules.length < 4) {
              const newModules = [...targetGroup.modules];
              const insertIndex = targetIndex !== undefined ? targetIndex : newModules.length;
              newModules.splice(insertIndex, 0, app);

              updateModuleGroup(targetGroupId, {
                ...targetGroup,
                modules: newModules.map((m) => ({ id: m.id, name: m.name })),
              });
            }
          }
        } else if (dragData.type === "group") {
          const sourceIndex = groups.findIndex((g) => g.id === dragData.id);
          const targetIdx = targetGroupId ? groups.findIndex((g) => g.id === targetGroupId) : -1;

          if (sourceIndex !== -1 && targetIdx !== -1 && sourceIndex !== targetIdx) {
            const newGroups = [...groups];
            const [draggedGroup] = newGroups.splice(sourceIndex, 1);
            newGroups.splice(targetIdx, 0, draggedGroup);

            reorderModuleGroups(newGroups.map((g) => g.id));
          }
        }
      } catch (error) {
        console.error("Drop error:", error);
      }
    },
    [pinnedApps, groups, updateModuleGroup, reorderModuleGroups]
  );

  const getDropIndex = useCallback((e, container) => {
    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const children = Array.from(container.children).filter(
      (child) => child.getAttribute("data-app") === "true"
    );

    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      const childRect = child.getBoundingClientRect();
      const childX = childRect.left - rect.left + childRect.width / 2;

      if (mouseX < childX) {
        return i;
      }
    }

    return children.length;
  }, []);

  const handleCreateGroup = () => {
    if (groups.length >= 10) {
      setSnackbar({
        open: true,
        message: "Máximo de 10 grupos permitidos",
        severity: "warning",
      });
      return;
    }

    const newGroup = {
      id: `group-${Date.now()}`,
      name: `Grupo ${groups.length + 1}`,
      color: GROUP_COLORS[groups.length % GROUP_COLORS.length] || GROUP_COLORS[0],
      modules: [],
    };

    createModuleGroup(newGroup);
  };

  const handleGroupClick = (groupId) => {
    const newExpandedId = expandedGroupId === groupId ? null : groupId;
    setActiveGroup(newExpandedId);
  };

  const handleMenuOpen = (event, groupId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedGroupId(groupId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedGroupId(null);
  };

  const handleStartEditing = (groupId, currentName) => {
    setEditingGroupId(groupId);
    setEditingName(currentName);
    handleMenuClose();
  };

  const handleFinishEditing = () => {
    if (editingGroupId && editingName.trim()) {
      const group = groups.find((g) => g.id === editingGroupId);
      if (group) {
        updateModuleGroup(editingGroupId, {
          ...group,
          name: editingName.trim(),
          modules: group.modules.map((m) => ({ id: m.id, name: m.name })),
        });
      }
    }
    setEditingGroupId(null);
    setEditingName("");
  };

  const handleChangeGroupColor = (groupId, color) => {
    const group = groups.find((g) => g.id === groupId);
    if (group) {
      updateModuleGroup(groupId, {
        ...group,
        color,
        modules: group.modules.map((m) => ({ id: m.id, name: m.name })),
      });
    }
    handleMenuClose();
  };

  const handleDeleteGroup = (groupId) => {
    setConfirmDialog({
      open: true,
      title: "Eliminar grupo",
      message: "¿Eliminar este grupo? Los módulos volverán a no estar agrupados.",
      onConfirm: () => {
        deleteModuleGroup(groupId);
        if (expandedGroupId === groupId) {
          setActiveGroup(null);
        }
        setConfirmDialog({ open: false, title: "", message: "", onConfirm: null });
      },
    });
    handleMenuClose();
  };

  const handleModuleClick = (app) => {
    if (!isDragging) {
      accessApp(app.id);
      if (onAppClick) {
        onAppClick(app);
      }
    }
  };

  const getCategoryColor = (app) => {
    if (settings.appColors?.[app.id]) {
      return settings.appColors[app.id];
    }
    const category = categories?.find((cat) => cat.id === app.categoryId);
    if (category && settings.categoryColors?.[category.id]) {
      return settings.categoryColors[category.id];
    }
    return category?.color || app.color || "#757575";
  };

  if (settings.navigationMode !== "groups") {
    return null;
  }

  const selectedGroup = groups.find((g) => g.id === selectedGroupId);

  return (
    <Box
      className={className}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flex: 1,
      }}
    >
      {/* Círculos de grupos */}
      {groups.map((group) => {
        const IconComponent = group.modules[0]?.icon;
        return (
          <Tooltip
            key={group.id}
            title={
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {group.name}
                </Typography>
                <Typography variant="caption">
                  {group.modules.length} módulo{group.modules.length !== 1 ? "s" : ""}
                </Typography>
                {group.modules.length > 0 && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    {group.modules.map((m) => m.name).join(", ")}
                  </Typography>
                )}
              </Box>
            }
            arrow
          >
            <Box
              sx={{
                position: "relative",
                "&:hover .group-menu-button": {
                  opacity: 1,
                },
              }}
            >
              <Box
                onClick={() => handleGroupClick(group.id)}
                onDragOver={handleDragOver}
                onDragEnter={() => setDragOverTarget(group.id)}
                onDragLeave={() => setDragOverTarget(null)}
                onDrop={(e) => handleDrop(e, group.id)}
                draggable={false}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  backgroundColor: group.color,
                  border: `2px solid ${
                    expandedGroupId === group.id
                      ? isDarkMode
                        ? "rgba(255, 255, 255, 0.3)"
                        : "rgba(0, 0, 0, 0.3)"
                      : "transparent"
                  }`,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  transform: expandedGroupId === group.id ? "scale(1.1)" : "scale(1)",
                  boxShadow:
                    expandedGroupId === group.id
                      ? "0 2px 8px rgba(0, 0, 0, 0.2)"
                      : dragOverTarget === group.id
                      ? "0 0 0 2px #3b82f6"
                      : "none",
                  "&:hover": {
                    transform: "scale(1.1)",
                  },
                }}
              />
              <IconButton
                className="group-menu-button"
                size="small"
                onClick={(e) => handleMenuOpen(e, group.id)}
                sx={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  width: 16,
                  height: 16,
                  opacity: 0,
                  transition: "opacity 0.2s ease",
                  backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
                  border: `1px solid ${isDarkMode ? "#404040" : "#e0e0e0"}`,
                  "&:hover": {
                    backgroundColor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
                  },
                }}
              >
                <MoreHorizIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
          </Tooltip>
        );
      })}

      {/* Botón para crear nuevo grupo */}
      {groups.length < 4 && (
        <Tooltip title="Crear nuevo grupo (máx. 4)" arrow>
          <IconButton
            onClick={handleCreateGroup}
            size="small"
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
              opacity: 0.6,
              "&:hover": {
                opacity: 1,
                backgroundColor: isDarkMode ? "#404040" : "#e0e0e0",
              },
            }}
          >
            <AddIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Grupo expandido */}
      {expandedGroupId && (() => {
        const expandedGroup = groups.find((g) => g.id === expandedGroupId);
        console.log("[ModuleGroups] Rendering expanded group:", expandedGroup);
        if (!expandedGroup) {
          console.log("[ModuleGroups] Expanded group not found for ID:", expandedGroupId, "Available groups:", groups.map(g => g.id));
          return null;
        }

        return (
          <>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: isDarkMode ? "#404040" : "#e0e0e0", mx: 1 }}
            />
            <Box
              onDragOver={handleDragOver}
              onDragEnter={() => setDragOverTarget(expandedGroupId)}
              onDragLeave={() => setDragOverTarget(null)}
              onDrop={(e) => {
                const targetIndex = getDropIndex(e, e.currentTarget);
                handleDrop(e, expandedGroupId, targetIndex);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
                border: `1px solid ${expandedGroup.color}40`,
                boxShadow: dragOverTarget === expandedGroupId ? "0 0 0 2px #3b82f6" : "none",
              }}
            >
              {settings.showGroupNames && (
                <Typography
                  variant="caption"
                  sx={{
                    color: isDarkMode ? "#b0b0b0" : "text.secondary",
                    mr: 1,
                  }}
                >
                  {expandedGroup.name}:
                </Typography>
              )}
              {expandedGroup.modules.length > 0 ? (
                expandedGroup.modules.map((module, index) => {
                  const categoryColor = getCategoryColor(module);
                  const IconComponent = module.icon;

                  return (
                    <Tooltip key={module.id} title={module.name} arrow>
                      <Box
                        data-app="true"
                        onClick={() => handleModuleClick(module)}
                        onDragStart={(e) =>
                          handleDragStart(e, {
                            type: "app",
                            id: module.id,
                            sourceGroupId: expandedGroupId,
                            sourceIndex: index,
                          })
                        }
                        onDragEnd={handleDragEnd}
                        draggable
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          backgroundColor:
                            settings.topBarIconStyle === "colored" ? categoryColor : "transparent",
                          color: settings.topBarIconStyle === "colored" ? "#ffffff" : "inherit",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          position: "relative",
                          "&:hover": {
                            transform: "scale(1.05)",
                            backgroundColor:
                              settings.topBarIconStyle === "colored"
                                ? categoryColor
                                : isDarkMode
                                ? "rgba(255, 255, 255, 0.1)"
                                : "rgba(0, 0, 0, 0.05)",
                          },
                        }}
                      >
                        <IconComponent sx={{ fontSize: 16 }} />
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: 24,
                            height: 2,
                            borderRadius: 1,
                            backgroundColor: expandedGroup.color,
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeModuleFromGroup(module.id);
                          }}
                          sx={{
                            position: "absolute",
                            top: -4,
                            right: -4,
                            width: 12,
                            height: 12,
                            opacity: 0,
                            transition: "opacity 0.2s ease",
                            backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
                            border: `1px solid ${isDarkMode ? "#404040" : "#e0e0e0"}`,
                            "&:hover": {
                              backgroundColor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
                            },
                            "&:hover, .MuiBox-root:hover &": {
                              opacity: 1,
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 8 }} />
                        </IconButton>
                      </Box>
                    </Tooltip>
                  );
                })
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    color: isDarkMode ? "#b0b0b0" : "text.secondary",
                    fontStyle: "italic",
                  }}
                >
                  Grupo vacío - arrastra apps aquí
                </Typography>
              )}
            </Box>
          </>
        );
      })()}

      {/* Apps no agrupadas */}
      {ungroupedApps.length > 0 && (
        <>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: isDarkMode ? "#404040" : "#e0e0e0", mx: 1 }}
          />
          <Box
            onDragOver={handleDragOver}
            onDragEnter={() => setDragOverTarget("ungrouped")}
            onDragLeave={() => setDragOverTarget(null)}
            onDrop={(e) => {
              const targetIndex = getDropIndex(e, e.currentTarget);
              handleDrop(e, undefined, targetIndex);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              boxShadow: dragOverTarget === "ungrouped" ? "0 0 0 2px #3b82f6" : "none",
            }}
          >
            {ungroupedApps.map((app, index) => {
              const categoryColor = getCategoryColor(app);
              const IconComponent = app.icon;

              return (
                <Tooltip key={app.id} title={`${app.name} - Arrastra para agrupar`} arrow>
                  <Box
                    data-app="true"
                    onClick={() => handleModuleClick(app)}
                    onDragStart={(e) =>
                      handleDragStart(e, {
                        type: "app",
                        id: app.id,
                        sourceIndex: index,
                      })
                    }
                    onDragEnd={handleDragEnd}
                    draggable
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      backgroundColor:
                        settings.topBarIconStyle === "colored" ? categoryColor : "transparent",
                      color: settings.topBarIconStyle === "colored" ? "#ffffff" : "inherit",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "scale(1.05)",
                        backgroundColor:
                          settings.topBarIconStyle === "colored"
                            ? categoryColor
                            : isDarkMode
                            ? "rgba(255, 255, 255, 0.1)"
                            : "rgba(0, 0, 0, 0.05)",
                      },
                    }}
                  >
                    <IconComponent sx={{ fontSize: 16 }} />
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </>
      )}

      {/* Menú de opciones del grupo */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
            color: isDarkMode ? "#e0e0e0" : "#1a1a1a",
            minWidth: 200,
          },
        }}
      >
        {editingGroupId === selectedGroupId ? (
          <Box sx={{ p: 1 }}>
            <TextField
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onBlur={handleFinishEditing}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleFinishEditing();
                } else if (e.key === "Escape") {
                  setEditingGroupId(null);
                  setEditingName("");
                }
              }}
              autoFocus
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: isDarkMode ? "#2d2d2d" : "#ffffff",
                  color: isDarkMode ? "#e0e0e0" : "#1a1a1a",
                },
              }}
            />
          </Box>
        ) : (
          <>
            <MenuItem
              onClick={() => handleStartEditing(selectedGroupId, selectedGroup?.name || "")}
              sx={{ color: isDarkMode ? "#e0e0e0" : "#1a1a1a" }}
            >
              Editar nombre
            </MenuItem>
            <Divider sx={{ borderColor: isDarkMode ? "#404040" : "#e0e0e0" }} />
            <Box sx={{ p: 1 }}>
              <Typography variant="caption" sx={{ color: isDarkMode ? "#b0b0b0" : "text.secondary", mb: 1, display: "block" }}>
                Color del grupo:
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {GROUP_COLORS.map((color) => (
                  <Box
                    key={color}
                    onClick={() => handleChangeGroupColor(selectedGroupId, color)}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      backgroundColor: color,
                      border: `2px solid ${
                        selectedGroup?.color === color
                          ? isDarkMode
                            ? "#ffffff"
                            : "#000000"
                          : "transparent"
                      }`,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "scale(1.1)",
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
            <Divider sx={{ borderColor: isDarkMode ? "#404040" : "#e0e0e0" }} />
            <MenuItem
              onClick={handleMenuClose}
              disabled={selectedGroup?.modules.length >= 4}
              sx={{ color: isDarkMode ? "#e0e0e0" : "#1a1a1a" }}
            >
              Agregar módulo {selectedGroup?.modules.length >= 4 && "(Máx. 4)"}
            </MenuItem>
            <Divider sx={{ borderColor: isDarkMode ? "#404040" : "#e0e0e0" }} />
            <MenuItem
              onClick={() => handleDeleteGroup(selectedGroupId)}
              sx={{ color: "#f44336" }}
            >
              <DeleteIcon sx={{ fontSize: 16, mr: 1 }} />
              Eliminar grupo
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Diálogo de confirmación */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, title: "", message: "", onConfirm: null })}
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
            color: isDarkMode ? "#e0e0e0" : "#1a1a1a",
          },
        }}
      >
        <DialogTitle sx={{ color: isDarkMode ? "#e0e0e0" : "#1a1a1a" }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: isDarkMode ? "#b0b0b0" : "text.secondary" }}>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, title: "", message: "", onConfirm: null })}
            sx={{ color: isDarkMode ? "#e0e0e0" : "#1a1a1a" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              if (confirmDialog.onConfirm) {
                confirmDialog.onConfirm();
              }
            }}
            variant="contained"
            color="error"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ModuleGroups;

